import React, { memo, useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';

import {
	Button,
	Checkbox,
	Input,
	Meta,
	TextArea,
} from '@components/common';
import { ETabsIds } from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/tabsIds';

import { isEmpty } from '@utils/common';
import { stringToNormalLock } from '@utils/strings/strings';

import { IAddKpiNotificationChannelProps } from '../interfaces';

import { ESeverity } from '@enums/kpi-common';
import {
	EKpiBasicInformationFields,
} from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/AddKpiAlertBasicInformation/enums';
import { EChannelFields } from './enums';

import { mandatoryErrMessage } from '@constants/validationConstants';

import Label from '../../../../../common/Label';

import styles from './AddKpiAlertChannel.module.scss';

function AddKpiAlertChannel({
	formMethods,
	formState,
	values,
	positiveHandler,
	negativeHandler,
	isEdit,
	negativeBtnText,
}: IAddKpiNotificationChannelProps) {
	const bodyRef = useRef<HTMLTextAreaElement | null>();
	const textAreaRefContainer = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (textAreaRefContainer?.current) {
			bodyRef.current = textAreaRefContainer.current.querySelector('textarea');
		}
	}, []);

	const {
		setValue,
		watch,
		register,
		setError,
		clearErrors,
		getValues,
		handleSubmit,
	} = formMethods;

	const { errors } = formState;

	const isEmail = watch(EChannelFields.isEmail);
	const isNotification = watch(EChannelFields.isNotification);
	const messageTitle = watch(EChannelFields.messageTitle);
	const messageBody = watch(EChannelFields.messageBody);

	const onChangeIsEmail = useCallback(() => {
		const { isEmail } = getValues();

		setValue(EChannelFields.isEmail, !isEmail);

		const values = getValues();

		if (!values.isEmail && !values.isNotification) {
			setError(EChannelFields.isEmail, { message: mandatoryErrMessage('Channel') });
			setError(EChannelFields.isNotification, { message: mandatoryErrMessage('Channel') });
		} else if (values.isEmail) {
			clearErrors(EChannelFields.isEmail);
			clearErrors(EChannelFields.isNotification);
		}
	}, [clearErrors, getValues, setError, setValue]);

	const onChangeIsNotification = useCallback(() => {
		const { isNotification } = getValues();

		setValue(EChannelFields.isNotification, !isNotification);

		const values = getValues();

		if (!values.isEmail && !values.isNotification) {
			setError(EChannelFields.isEmail, { message: mandatoryErrMessage('Channel') });
			setError(EChannelFields.isNotification, { message: mandatoryErrMessage('Channel') });
		} else if (values.isNotification) {
			clearErrors(EChannelFields.isEmail);
			clearErrors(EChannelFields.isNotification);
		}
	}, [clearErrors, getValues, setError, setValue]);

	const notificationTypeClassNames = cn(styles.notificationType, {
		[styles.critical]: values[EKpiBasicInformationFields.severity] === ESeverity.critical,
		[styles.informational]: values[EKpiBasicInformationFields.severity] === ESeverity.informational,
		[styles.warning]: values[EKpiBasicInformationFields.severity] === ESeverity.warning,
		[styles.error]: values[EKpiBasicInformationFields.severity] === ESeverity.error,
	});

	const onInvalid = () => {
		const values = getValues();

		if (!values.isEmail && !values.isNotification) {
			setError(EChannelFields.isEmail, { message: mandatoryErrMessage('Channel') });
			setError(EChannelFields.isNotification, { message: mandatoryErrMessage('Channel') });
			return;
		}

		if (!values.isEmail && values.isNotification) {
			clearErrors(EChannelFields.isEmail);
		}

		if (values.isEmail && !values.isNotification) {
			clearErrors(EChannelFields.isNotification);
		}
	};

	const setBodyRefFocus = () => {
		if (bodyRef.current) {
			bodyRef.current.focus();
		}
	};

	const onBackHandler = useCallback(() => {
		negativeHandler(ETabsIds.recipient);
	}, [negativeHandler]);

	const onSave = () => {
		handleSubmit(positiveHandler, onInvalid)();
	};

	return (
		<div className='channel flex-column flex-gap_30'>
			<>
				<div className='fieldLabel'>
					<span>Channel</span>
					<span className='asterisk ml-5'>*</span>
				</div>
				<div className='fieldsContainer flex-column flex-gap_05'>
					<div className='display-flex flex-column flex-gap_10'>
						<Checkbox
							checked={isEmail}
							disabled={isEdit}
							isError={!!errors?.isEmail?.message}
							label='Email'
							name={EChannelFields.isEmail}
							onChange={onChangeIsEmail}
						/>
						<Checkbox
							checked={isNotification}
							disabled={isEdit}
							isError={!!errors?.isEmail?.message}
							label='Notification'
							name={EChannelFields.isNotification}
							onChange={onChangeIsNotification}
						/>
					</div>
					{(!!errors?.isEmail?.message || !!errors?.isNotification?.message) && (
						<Meta
							className='f5gInputMeta'
							message={errors?.isEmail?.message || errors?.isNotification?.message as string}
						/>
					)}
				</div>
			</>
			<div
				ref={textAreaRefContainer}
				className='display-flex flex-column flex-gap_15'
			>
				<div className='fieldLabel'>
					<span>Message</span>
				</div>
				<Input
					disabled={isEdit}
					errorMessage={errors?.messageTitle?.message}
					isError={!!errors?.messageTitle?.message}
					label='Title'
					name={EChannelFields.messageTitle}
					placeholder='Enter title'
					register={register}
					required
					value={messageTitle}
					withUseForms
				/>
				<div>
					<Label
						className={styles.bodyLabel}
						label='Body'
						required
					/>
					<div
						className={styles.fakeTextAreaContainer}
						onClick={setBodyRefFocus}
					>
						<div className={cn(styles.mockedText, {
							[styles.disabled]: isEdit,
						})}>
							<p>
								<span>Hi,</span>
								<br />
								<br />
								<span className={notificationTypeClassNames}>
									{stringToNormalLock(values[EKpiBasicInformationFields.severity])}
								</span>
								<br />
								<br />
								<span>
									The following notification rule has been triggered at &lt;HH:MM dd/mm/yyyy&gt;:
								</span>
								<br />
								&bull;&lt;KPI name&gt; is &lt;operator1&gt; &lt;value1&gt;
							</p>
						</div>
						<TextArea
							className={styles.customTextAria}
							disabled={isEdit}
							isNotResizable
							isTransparent
							name={EChannelFields.messageBody}
							placeholder='Enter message body'
							register={register as any}
							required
							rows={10}
							value={messageBody}
							withUseForms
						/>
					</div>
					{errors?.messageBody?.message && <Meta message={errors.messageBody.message} />}
				</div>
			</div>
			<div className='buttonsContainer'>
				<Button
					onClick={onBackHandler}
					text={negativeBtnText}
					withoutBackground
				/>
				<Button
					disabled={!isEmpty(errors)}
					onClick={onSave}
					text='Save'
				/>
			</div>
		</div>
	);
}

export default memo(AddKpiAlertChannel);
